import classNames from "classnames";
import { Divider } from "PFComponents/divider";
import { LoadingDots } from "PFComponents/loading_dots";
import { Select } from "PFComponents/select/select";
import { Typography } from "PFComponents/typography";
import { useProfilesBookings, useProfilesWarnings } from "PFCore/hooks/queries/profile";
import { Profile } from "PFTypes";
import { useTranslation } from "react-i18next";

import { useCalendarScrolling } from "../../../../parts/hooks";
import { useCalendarContext } from "../../../../parts/overview/context/calendar_context";
import css from "../../../../parts/overview/detail_view/detail_view.module.scss";
import { useBookingModuleContext } from "../../../../parts/providers/booking_module_context_provider";
import { DetailsPanelData } from "../../details_panel_context_provider";
import { Overbooking } from "./overbooking";
import cssOverbookingDetail from "./overbooking_detail.module.scss";
import { useOverbookingRanges } from "./use_overbooking_ranges";

type OverbookingsDetailProps = {
  profile: Profile | undefined;
  isProfileLoading: boolean;
  data: DetailsPanelData;
  onDeleted: (entity: any) => Promise<void>;
  onEdited: () => void;
};

export const OverbookingsDetail = ({
  profile,
  isProfileLoading,
  data,
  onDeleted,
  onEdited
}: OverbookingsDetailProps) => {
  const { calendarRange } = useBookingModuleContext();
  const { calendarHeaderRef, calendarScrollWrapperRef } = useCalendarContext();

  const { scrollToDate } = useCalendarScrolling({
    calendarHeaderRef,
    calendarScrollWrapperRef
  });

  const { t } = useTranslation("bookingModule", { keyPrefix: "details.overbookings" });
  const { warningsByProfile, isLoading: isWarningsLoading } = useProfilesWarnings(
    profile ? [{ id: profile.id }] : [],
    calendarRange,
    !!profile
  );

  const rowKey = `profile_{data.id}`;
  const { bookingsByRowKey } = useProfilesBookings({
    profiles: profile ? [{ id: profile?.id, rowKey }] : [],
    dateRange: calendarRange,
    enabled: !!profile
  });
  const bookings = bookingsByRowKey[rowKey];

  const overbookings = profile && !isWarningsLoading ? warningsByProfile[profile.id].overbookings : [];
  const { rangesWithBookings, selectedSort, setSelectedSort, sortOptions } = useOverbookingRanges({
    overbookings,
    bookings
  });

  if (!profile || isProfileLoading || isWarningsLoading) {
    return <LoadingDots />;
  }

  return (
    <div className={classNames(css.root)}>
      <Divider className={css.separator} color="paletteNeutral0" size={2} />
      <Select
        // TODO: [PROF-2906] - remove suppressing when select rewritten to ts
        /* @ts-ignore */
        rootClassName={cssOverbookingDetail.select}
        controlledValue
        options={sortOptions}
        value={sortOptions.find(({ item }) => item === selectedSort)?.displayElement}
        handleChange={setSelectedSort}
      />
      <Typography variant="labelRegular" className={css.subtitle}>
        {t("ranges", { count: rangesWithBookings.length })}
      </Typography>
      {rangesWithBookings.map(({ start, end, bookings, overbookings }) => {
        const overbookingKey = `${profile.id}_${start}`;
        return (
          <Overbooking
            key={overbookingKey}
            start={start}
            end={end}
            bookings={bookings}
            profile={profile}
            bars={data?.bars}
            overbookings={overbookings}
            scrollToDate={scrollToDate}
            onDeleted={onDeleted}
            onEdited={onEdited}
          />
        );
      })}
    </div>
  );
};
